import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useHopLocal, hopCounts, HopPackLocalLine, HopWaitingChip } from "./HopLocalStatus";   // 10 Oct 2026 — today on this phone
import {
  ArrowLeft, Camera, FileText, FolderKanban, CheckCircle2,
  AlertTriangle, ClipboardList, Tag, ChevronRight, Send, ThumbsUp,
  XCircle, Wrench, Trash2, Plus, X, Edit3, ChevronDown, Share2,
} from "lucide-react";
import HopShareModal from "./HopShareModal";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { useAcceptanceBasket } from "@/features/acceptance-hub/useAcceptanceBasket";
import { ShieldCheck } from "lucide-react";
import { Card, Pill, Spinner, EmptyState, Button, Textarea } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// HopPackScreen — /projects/:projectId/hop/:packId
// T-010 Commit C.3b — reads from PACK-OWNED categories (set by the wizard at
// creation time), not the template library. Shows the curated scope for this
// specific pack: only the categories the manager ticked, with their declared
// instances, against the label prefix that drives export filenames.
//
// Capture UX (camera + watermark + upload) lands in C.2 — until then the
// screen is a structural read-only view + a banner pointing at C.2.
// 10 Oct 2026 — "today on this phone": a line under the header (taken /
// uploaded / on this phone) and an amber "N on phone" chip on each category
// and subcategory that still has photos waiting. Read from the phone
// (HopLocalStatus), so it is right with no signal.
// =============================================================================

type PackStatus = "open" | "submitted" | "accepted" | "defects_raised";

interface Pack {
  pack_id:            string;
  pack_number:        string;
  title:              string | null;
  notes:              string | null;
  status:             PackStatus;
  label_prefix:       string | null;
  created_at:         string;
  submitted_at:       string | null;
  accepted_at:        string | null;
  defects_raised_at:  string | null;
  defects_notes:      string | null;
  is_template_locked: boolean | null;     // T-219 — null treated as unlocked
  template?:          { template_code: string; display_name: string; description: string | null } | null;
  project?:           {
    project_id: string; project_code: string; project_name: string;
    site?: { site_code: string | null; site_name: string | null } | null;
  } | null;
}

// Heading for one pack: WHERE it is, then WHAT it is.
// Site name first (that is how the crew and the client both refer to the job),
// falling back to the site code, then the project name if the project has no
// site attached. The pack's own title is its name; a pack saved without a
// title falls back to its number so the heading is never the template type.
function packHeading(pack: Pack): string {
  const site  = pack.project?.site ?? null;
  const where = site?.site_name?.trim() || site?.site_code?.trim() || pack.project?.project_name?.trim() || "";
  const what  = pack.title?.trim() || pack.pack_number || "HOP pack";
  return where ? `${where} — ${what}` : what;
}

interface PackCategory {
  pack_category_id: string;
  code:         string;
  display_name: string;
  description:  string | null;
  required_qty: number;
  sort_order:   number;
  is_custom:    boolean;
}

interface PackSubcategory {
  pack_subcategory_id: string;
  pack_category_id:    string;
  code:                string;
  display_name:        string;
  hint:                string | null;
  allow_free_text:     boolean;
  sort_order:          number;
  is_custom:           boolean;
}

interface PackInstance {
  instance_id:      string;
  pack_category_id: string;
  instance_code:    string;
  instance_label:   string | null;
  sort_order:       number;
}

interface AssetCount {
  pack_category_id: string;
  count:            number;
}

const PACK_TONE: Record<PackStatus, "open" | "pending" | "approved" | "rejected"> = {
  open:           "open",
  submitted:      "pending",
  accepted:       "approved",
  defects_raised: "rejected",
};

const PACK_LABEL: Record<PackStatus, string> = {
  open:           "in progress",
  submitted:      "awaiting review",
  accepted:       "accepted",
  defects_raised: "defects raised",
};

export default function HopPackScreen() {
  const { projectId, packId } = useParams<{ projectId: string; packId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = hasRole(user, "manager");
  // T-219 — categories editor modal
  const [showCategoriesEditor, setShowCategoriesEditor] = useState(false);
  const [showDefectsModal, setShowDefectsModal] = useState(false);
  const [defectsText, setDefectsText] = useState("");
  // T-320 — edit pack title + label prefix (the prefix drives the export
  // filename front-end, e.g. SA000355_Nullarbor_RT_...).
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editPrefix, setEditPrefix] = useState("");
  const [editErr, setEditErr] = useState<string | null>(null);
  // Client portal — manager+ can open the share modal from any pack, any status.
  const [portalOpen, setPortalOpen] = useState(false);

  // T-160 — basket indicator (visible on every HOP pack screen)
  const { basketSize } = useAcceptanceBasket(projectId);

  const { data: pack, isLoading: loadingPack } = useQuery({
    queryKey: ["hop_pack", packId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`
          pack_id, pack_number, title, notes, status, label_prefix, created_at,
          submitted_at, accepted_at, defects_raised_at, defects_notes,
          is_template_locked,
          template:template_id(template_code, display_name, description),
          project:project_id(project_id, project_code, project_name,
                             site:site_id(site_code, site_name))
        `)
        .eq("pack_id", packId!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as Pack | null;
    },
  });

  // T-320 — save pack title + label prefix.
  const saveDetailsMut = useMutation({
    mutationFn: async () => {
      const prefix = editPrefix.trim();
      if (!prefix) throw new Error("Label prefix is required — it leads every photo filename.");
      const { error } = await supabase
        .from("hop_packs")
        .update({ title: editTitle.trim() || null, label_prefix: prefix })
        .eq("pack_id", packId!);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      setShowEditDetails(false); setEditErr(null);
      qc.invalidateQueries({ queryKey: ["hop_pack", packId] });
    },
    onError: (e: any) => setEditErr(e.message ?? "Save failed"),
  });

  const { data: categories = [], isLoading: loadingCats } = useQuery({
    queryKey: ["hop_pack_categories_for_pack", packId],
    enabled: !!pack,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_pack_categories")
        .select("pack_category_id, code, display_name, description, required_qty, sort_order, is_custom")
        .eq("pack_id", packId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PackCategory[];
    },
  });

  const { data: subcats = [] } = useQuery({
    queryKey: ["hop_pack_subcategories_for_pack", packId, categories.map(c => c.pack_category_id).join(",")],
    enabled: categories.length > 0,
    queryFn: async () => {
      const catIds = categories.map(c => c.pack_category_id);
      const { data, error } = await supabase
        .from("hop_pack_subcategories")
        .select("pack_subcategory_id, pack_category_id, code, display_name, hint, allow_free_text, sort_order, is_custom, is_na")
        .in("pack_category_id", catIds)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PackSubcategory[];
    },
  });

  const { data: packInstances = [] } = useQuery({
    queryKey: ["hop_pack_instances_for_pack", packId, categories.map(c => c.pack_category_id).join(",")],
    enabled: categories.length > 0,
    queryFn: async () => {
      const catIds = categories.map(c => c.pack_category_id);
      const { data, error } = await supabase
        .from("hop_pack_category_instances")
        .select("instance_id, pack_category_id, instance_code, instance_label, sort_order")
        .in("pack_category_id", catIds)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as PackInstance[];
    },
  });

  // 10 Oct 2026 — what this phone holds for this pack (works with no signal)
  const hopLocalData = useHopLocal(packId);

  const { data: assetCounts = [] } = useQuery({
    queryKey: ["hop_asset_counts", packId],
    enabled: !!packId,
    queryFn: async () => {
      // Group-by client-side to avoid an RPC. Counts photos per pack-category.
      const { data, error } = await supabase
        .from("hop_assets")
        .select("pack_category_id")
        .eq("pack_id", packId!);
      if (error) throw error;
      const map = new Map<string, number>();
      for (const a of data ?? []) {
        if (!a.pack_category_id) continue;
        map.set(a.pack_category_id, (map.get(a.pack_category_id) ?? 0) + 1);
      }
      return Array.from(map, ([pack_category_id, count]) => ({ pack_category_id, count })) as AssetCount[];
    },
  });

  // T-280 — per-subcategory photo counts. Drives the colour-code on the
  // subcategory cards in the pack overview so the user can see at-a-glance
  // which subcats still need photos (filled = has photos, empty = pending).
  // Same client-side group-by pattern as assetCounts.
  const { data: subcatCounts = new Map<string, number>() } = useQuery({
    queryKey: ["hop_subcat_counts", packId],
    enabled: !!packId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_assets")
        .select("pack_subcategory_id")
        .eq("pack_id", packId!);
      if (error) throw error;
      const map = new Map<string, number>();
      for (const a of data ?? []) {
        if (!a.pack_subcategory_id) continue;
        map.set(a.pack_subcategory_id, (map.get(a.pack_subcategory_id) ?? 0) + 1);
      }
      return map;
    },
  });

  // Action mutations — submit / accept / raise-defects all go through
  // app-schema RPCs (migration 043) for atomic status flip + notifications.
  function refreshPack() {
    qc.invalidateQueries({ queryKey: ["hop_pack", packId] });
    qc.invalidateQueries({ queryKey: ["hop_packs", projectId] });
    qc.invalidateQueries({ queryKey: ["field_hop_packs", projectId] });
  }

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!packId) throw new Error("Missing pack id");
      const { error } = await supabase.schema("app").rpc("submit_hop_pack", { p_pack_id: packId });
      if (error) throw error;
    },
    onSuccess: refreshPack,
    onError: (e: any) => alert(e?.message ?? "Submit failed"),
  });

  const acceptMut = useMutation({
    mutationFn: async () => {
      if (!packId) throw new Error("Missing pack id");
      const { error } = await supabase.schema("app").rpc("accept_hop_pack", { p_pack_id: packId });
      if (error) throw error;
    },
    onSuccess: refreshPack,
    onError: (e: any) => alert(e?.message ?? "Accept failed"),
  });

  // T-220 v1 was built here at pack-level but the proper flow lives at
  // project-level via the existing acceptance-hub system (hop_submissions
  // table + NewHopSubmissionScreen + /accept/:token public URL). The
  // pack-level mutations + submission history that briefly lived here have
  // been removed. Migrations 138 + 140 are still in place but the
  // hop_pack_submissions table they created is now unused and will be
  // dropped in a future cleanup migration.

  const defectsMut = useMutation({
    mutationFn: async (notes: string) => {
      if (!packId) throw new Error("Missing pack id");
      const { error } = await supabase.schema("app").rpc("raise_hop_defects", {
        p_pack_id: packId,
        p_notes:   notes,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setShowDefectsModal(false);
      setDefectsText("");
      refreshPack();
    },
    onError: (e: any) => alert(e?.message ?? "Failed to raise defects"),
  });

  // Delete-empty-pack — manager+ only; server-side guard (migration 045)
  // rejects if any assets exist.
  const deleteMut = useMutation({
    mutationFn: async () => {
      if (!packId) throw new Error("Missing pack id");
      const { error } = await supabase.schema("app").rpc("delete_hop_pack", { p_pack_id: packId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hop_packs", projectId] });
      qc.invalidateQueries({ queryKey: ["field_hop_packs", projectId] });
      qc.invalidateQueries({ queryKey: ["hop_centre_kpis"] });
      qc.invalidateQueries({ queryKey: ["hop_centre_my"] });
      navigate(`/projects/${projectId}/hop`);
    },
    onError: (e: any) => alert(e?.message ?? "Delete failed"),
  });

  function confirmDelete() {
    if (!pack) return;
    if (!window.confirm(
      `Delete pack ${pack.pack_number}?\n\nThis pack has no photos or documents. ` +
      `It'll be removed permanently along with its category scope.`
    )) return;
    deleteMut.mutate();
  }

  function confirmSubmit() {
    if (!window.confirm("Submit this pack for manager review? You'll still be able to upload more after submitting if needed.")) return;
    submitMut.mutate();
  }

  function confirmAccept() {
    if (!window.confirm(`Accept pack ${pack?.pack_number}? The crew will be notified.`)) return;
    acceptMut.mutate();
  }

  const totals = useMemo(() => {
    const uploaded = assetCounts.reduce((a, r) => a + r.count, 0);
    const required = categories.reduce((a, c) => a + (c.required_qty ?? 0), 0);
    return { uploaded, required };
  }, [assetCounts, categories]);

  const subsByCategory = useMemo(() => {
    const m = new Map<string, PackSubcategory[]>();
    for (const s of subcats) {
      const arr = m.get(s.pack_category_id) ?? [];
      arr.push(s);
      m.set(s.pack_category_id, arr);
    }
    return m;
  }, [subcats]);

  const instancesByCategory = useMemo(() => {
    const m = new Map<string, PackInstance[]>();
    for (const i of packInstances) {
      const arr = m.get(i.pack_category_id) ?? [];
      arr.push(i);
      m.set(i.pack_category_id, arr);
    }
    return m;
  }, [packInstances]);

  if (!user) return null;
  if (loadingPack) {
    return <div className="p-6 flex justify-center"><Spinner size={20} /></div>;
  }
  if (!pack) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <Card>
          <EmptyState
            title="Pack not found"
            description="This HOP pack doesn't exist or you don't have access to it."
            icon={Camera}
          />
        </Card>
      </div>
    );
  }

  const tone = PACK_TONE[pack.status];
  const label = PACK_LABEL[pack.status];

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto pb-32">
      {/* T-160 basket indicator — always visible on the pack overview when
          something is in the basket. Same look as the category screen. */}
      {canManage && basketSize > 0 && (
        <div className="fixed right-4 z-40 bottom-[calc(var(--nav-clear)+var(--sticky-bar-h,0px)+1rem)] md:bottom-24 bg-ink-card border border-accent/40 rounded-lg shadow-xl p-3 flex items-center gap-3 max-w-xs">
          <div className="w-9 h-9 rounded-full bg-accent text-white flex items-center justify-center font-semibold">
            {basketSize}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium text-text leading-tight">
              Acceptance pack
            </div>
            <div className="text-[11px] text-text-muted">
              {basketSize} photo{basketSize === 1 ? "" : "s"}
            </div>
          </div>
          <Link to={`/projects/${projectId}/hop/submit/new`}>
            <button
              type="button"
              className="px-3 py-1.5 bg-accent text-white rounded text-[12px] font-medium inline-flex items-center gap-1 hover:bg-accent-hover"
            >
              <ShieldCheck size={12}/> Continue
            </button>
          </Link>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <button
          onClick={() => navigate(`/projects/${projectId}/hop`)}
          className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text"
          type="button"
        >
          <ArrowLeft size={14} /> HOP library
        </button>
        <Link
          to={`/acceptance/${packId}`}
          className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent"
        >
          <ClipboardList size={12}/> Review all photos in HOP Centre
        </Link>
      </div>

      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          {pack.project && (
            <div className="text-[11px] font-mono text-text-muted">
              {pack.project.project_code} · {pack.project.project_name}
            </div>
          )}
          {/* James, 22 Sep 2026: "Pack template type should not be the main
              heading here. should be the site name and pack name."
              The template type ("NBN Fixed Wireless — Golden Photo Pack 3.0")
              is the KIND of pack — identical words on every pack of that type,
              so the heading never told you which pack was open. Site + pack
              name lead now; the type stays on the line underneath. */}
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Camera size={20} className="text-accent" />
            {packHeading(pack)}
          </h1>
          <div className="text-text-muted text-[13px] mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono">{pack.pack_number}</span>
            {pack.template && (
              <span>· <span className="uppercase font-mono text-[11px]">{pack.template.template_code}</span> {pack.template.display_name}</span>
            )}
            {pack.label_prefix && (
              <span className="inline-flex items-center gap-1">
                · <Tag size={11} /> <span className="font-mono">{pack.label_prefix}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Pill label={label} tone={tone} />
          {canManage && (
            <Button
              variant="secondary"
              size="sm"
              icon={Edit3}
              onClick={() => {
                setEditTitle(pack.title ?? "");
                setEditPrefix(pack.label_prefix ?? "");
                setEditErr(null);
                setShowEditDetails(true);
              }}
            >
              Edit details
            </Button>
          )}
          {/* Acceptance documents — jump straight to the pack's Documents tab. */}
          <Link to={`/acceptance/${packId}?tab=documents`}>
            <Button variant="secondary" size="sm" icon={FileText}>Documents</Button>
          </Link>
          {/* Client portal — share photos to the client, any pack status. */}
          {canManage && (
            <Button
              variant="secondary"
              size="sm"
              icon={Share2}
              onClick={() => setPortalOpen(true)}
            >
              Share to client
            </Button>
          )}
          {/* Submit (any team member when open or defects_raised) */}
          {(pack.status === "open" || pack.status === "defects_raised") && (
            <Button
              size="sm"
              icon={Send}
              onClick={confirmSubmit}
              disabled={submitMut.isPending || totals.uploaded === 0}
              title={totals.uploaded === 0 ? "Upload at least one asset before submitting" : undefined}
            >
              {submitMut.isPending ? "Submitting…" : pack.status === "defects_raised" ? "Re-submit" : "Submit for review"}
            </Button>
          )}
          {/* Accept + raise defects (manager+ only when submitted) */}
          {pack.status === "submitted" && canManage && (
            <>
              <Button
                size="sm"
                icon={ThumbsUp}
                onClick={confirmAccept}
                disabled={acceptMut.isPending}
              >
                {acceptMut.isPending ? "Accepting…" : "Accept"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                icon={Wrench}
                onClick={() => setShowDefectsModal(true)}
                disabled={defectsMut.isPending}
              >
                Raise defects
              </Button>
            </>
          )}

          {/* T-220 v1 pack-level submit button removed — submission flow lives
              at project-level via the acceptance-hub (basket + NewHopSubmissionScreen). */}
          {/* Delete empty pack — manager+ only, only when zero assets AND
              pack status is still 'open'. Migration 046 enforces this
              server-side too. */}
          {canManage && totals.uploaded === 0 && pack.status === "open" && (
            <Button
              size="sm"
              variant="secondary"
              icon={Trash2}
              onClick={confirmDelete}
              disabled={deleteMut.isPending}
              className="text-danger hover:border-danger/50"
            >
              {deleteMut.isPending ? "Deleting…" : "Delete empty pack"}
            </Button>
          )}
        </div>
      </div>

      {/* Notes */}
      {pack.notes && (
        <Card className="mb-5">
          <div className="p-4">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">
              Pack notes
            </div>
            <p className="text-text text-[13px] whitespace-pre-wrap">{pack.notes}</p>
          </div>
        </Card>
      )}

      {/* Defects raised — visible to everyone, prominent for the field user
          to see what needs fixing. Cleared when the pack is accepted. */}
      {pack.status === "defects_raised" && pack.defects_notes && (
        <div className="mb-5 p-4 rounded bg-danger/10 border border-danger/30">
          <div className="flex items-start gap-2">
            <XCircle size={16} className="text-danger mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider2 text-danger font-mono mb-1 font-semibold">
                Defects raised — needs rework
              </div>
              <p className="text-text text-[13px] whitespace-pre-wrap">{pack.defects_notes}</p>
              <div className="text-[11px] text-text-muted mt-2">
                Fix the issues, upload any missing assets, then re-submit using the button up top.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <StatTile label="Categories" value={String(categories.length)} icon={FolderKanban} />
        <StatTile label="Uploaded"   value={String(totals.uploaded)}    icon={FileText} />
        <StatTile
          label="Required"
          value={totals.required > 0 ? `${Math.min(totals.uploaded, totals.required)}/${totals.required}` : "—"}
          icon={totals.required > 0 && totals.uploaded >= totals.required ? CheckCircle2 : ClipboardList}
          ok={totals.required > 0 && totals.uploaded >= totals.required}
        />
      </div>

      {/* T-219 — Edit categories button. Manager+ only. Hidden if pack is
          template-locked (NBN packs). */}
      {canManage && (
        <div className="flex justify-end mb-5">
          {pack.is_template_locked ? (
            <span
              className="text-[11px] text-text-dim italic"
              title="This pack uses a locked template (NBN). Categories can't be edited. Contact a director if you need a one-off override."
            >
              Categories locked (template-defined)
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setShowCategoriesEditor(true)}
              className="text-[12px] text-accent hover:text-accent-bright inline-flex items-center gap-1.5"
            >
              <FolderKanban size={12} /> Edit categories
            </button>
          )}
        </div>
      )}

      {/* Field capture is live — tap any category to upload */}
      <div className="mb-5 p-3 rounded bg-accent/10 border border-accent/30 text-[12px] text-accent flex items-start gap-2">
        <Camera size={13} className="mt-0.5 flex-shrink-0" />
        <div>
          <strong>Tap a category to upload.</strong> Camera capture, multi-select from gallery, PDF/document attach, watermarked client-side with GPS + timestamp + label prefix.
        </div>
      </div>

      {/* T-220 v1 submission history panel removed — submission history now
          surfaces in the Acceptance Centre per Step 3 of the rebuild. */}

      {/* Empty pack state */}
      {!loadingCats && categories.length === 0 && (
        <Card>
          <EmptyState
            title="This pack has no categories"
            description="Looks like the wizard didn't save any categories. Try creating the pack again."
            icon={ClipboardList}
          />
        </Card>
      )}

      {/* Raise-defects modal */}
      {showEditDetails && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center p-4"
          onClick={() => !saveDetailsMut.isPending && setShowEditDetails(false)}
        >
          <div className="bg-ink-card border border-line w-full md:max-w-lg rounded-lg p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-[16px] font-semibold flex items-center gap-2">
                <Tag size={15} className="text-accent" /> Edit pack details
              </h3>
              <button onClick={() => setShowEditDetails(false)} className="text-text-muted hover:text-text" disabled={saveDetailsMut.isPending}>×</button>
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-text-muted">Pack title</span>
                <input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  placeholder="e.g. Nullarbor Pack 2 - 15.097m to 22m"
                  className="mt-1 w-full bg-ink border border-line rounded px-3 py-2 text-[13px]"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-text-muted">Label prefix *</span>
                <input
                  value={editPrefix}
                  onChange={e => setEditPrefix(e.target.value)}
                  placeholder="e.g. SA000355_Nullarbor RT"
                  className="mt-1 w-full bg-ink border border-line rounded px-3 py-2 text-[13px] font-mono"
                />
                <span className="block mt-1 text-[11px] text-text-muted">
                  Leads every photo filename in this pack. Example:{" "}
                  <span className="font-mono text-text">
                    {(editPrefix.trim().replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "PREFIX")}_Frame_8_68_1.jpg
                  </span>
                </span>
              </label>
              {editErr && <div className="text-[12px] text-danger">{editErr}</div>}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <Button variant="secondary" onClick={() => setShowEditDetails(false)} disabled={saveDetailsMut.isPending}>Cancel</Button>
              <Button icon={CheckCircle2} onClick={() => saveDetailsMut.mutate()} disabled={saveDetailsMut.isPending || !editPrefix.trim()}>
                {saveDetailsMut.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDefectsModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center p-4"
          onClick={() => !defectsMut.isPending && setShowDefectsModal(false)}
        >
          <div className="bg-ink-card border border-line w-full md:max-w-lg rounded-lg p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-[16px] font-semibold flex items-center gap-2">
                <Wrench size={16} className="text-warn" />
                Raise defects
              </h3>
              <button
                onClick={() => setShowDefectsModal(false)}
                className="text-text-muted hover:text-text"
                disabled={defectsMut.isPending}
              >
                ×
              </button>
            </div>
            <div className="text-[12px] text-text-muted mb-3">
              Tell the crew what needs fixing — be specific. They'll see this on the pack and get a notification. Pack flips back to "defects raised" until they re-submit.
            </div>
            <Textarea
              rows={6}
              value={defectsText}
              onChange={e => setDefectsText(e.target.value)}
              placeholder="e.g. Construction_07 missing — need photo of grouting from sector 1 side. Earthing_03 photo too dark to read clamps."
              autoFocus
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => setShowDefectsModal(false)}
                disabled={defectsMut.isPending}
              >
                Cancel
              </Button>
              <Button
                icon={Wrench}
                onClick={() => defectsMut.mutate(defectsText)}
                disabled={defectsMut.isPending || !defectsText.trim()}
              >
                {defectsMut.isPending ? "Raising…" : "Raise defects"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <HopPackLocalLine local={hopLocalData} />

      {loadingCats ? (
        <div className="py-10 flex justify-center"><Spinner size={18} /></div>
      ) : (
        <div className="space-y-3">
          {categories.map(c => {
            const localCat = hopCounts(hopLocalData, "cat", c.pack_category_id);
            const count = assetCounts.find(ac => ac.pack_category_id === c.pack_category_id)?.count ?? 0;
            const required = c.required_qty ?? 0;
            const complete = required > 0 && count >= required;
            const subs = subsByCategory.get(c.pack_category_id) ?? [];
            const insts = instancesByCategory.get(c.pack_category_id) ?? [];
            return (
              <Link
                key={c.pack_category_id}
                to={`/projects/${projectId}/hop/${packId}/categories/${c.pack_category_id}`}
                className="block"
              >
                <Card className="hover:border-accent transition-colors group">
                  <div className="p-4 md:p-5">
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-text font-medium flex items-center gap-2 flex-wrap">
                          {c.display_name}
                          {c.is_custom && (
                            <span className="text-[10px] font-mono uppercase tracking-wider2 text-warn px-1.5 py-0.5 rounded bg-warn/10 border border-warn/30">custom</span>
                          )}
                        </div>
                        {c.description && (
                          <div className="text-[12px] text-text-muted mt-0.5">{c.description}</div>
                        )}
                        {insts.length > 0 && (
                          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">Instances:</span>
                            {insts.map(i => (
                              <span key={i.instance_id} className="px-2 py-0.5 rounded bg-accent/10 border border-accent/30 text-[11px] font-mono text-accent">
                                {i.instance_code}{i.instance_label ? ` · ${i.instance_label}` : ""}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <HopWaitingChip count={localCat.waiting} lost={localCat.lost} />
                        {/* T-280 — colour the count: green when complete, amber/warn
                            when there's a required minimum but it hasn't been met,
                            muted otherwise (no minimum specified). */}
                        <div className={`text-[12px] font-mono flex items-center gap-1.5 ${
                          complete                       ? "text-ok"
                          : required > 0 && count < required ? "text-warn"
                          : "text-text-muted"
                        }`}>
                          {complete && <CheckCircle2 size={12} />}
                          {required > 0 ? `${count}/${required}` : `${count} photo${count === 1 ? "" : "s"}`}
                        </div>
                        <ChevronRight size={14} className="text-text-muted group-hover:text-accent transition-colors" />
                      </div>
                    </div>
                    {subs.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-line grid grid-cols-1 md:grid-cols-2 gap-2">
                        {subs.map(s => {
                          // T-280 — colour-code subcat card by photo presence:
                          //  - has photos: light accent-tinted background + "✓ N" badge
                          //  - empty:      current neutral look
                          const subCount = subcatCounts.get(s.pack_subcategory_id) ?? 0;
                          const hasPhotos = subCount > 0;
                          // T-413 polish (James) — N/A subcategories grey out
                          // here to match the category screen, so crews can't
                          // mistake them for photos still owed.
                          const isNa = !!(s as any).is_na;
                          return (
                            <div
                              key={s.pack_subcategory_id}
                              className={`p-2 rounded border ${
                                isNa
                                  ? "bg-ink-raised/40 border-line/50 opacity-50"
                                  : hasPhotos
                                    ? "bg-accent/5 border-accent/30"
                                    : "bg-ink-raised border-line"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-1.5 text-[12px] flex-wrap min-w-0">
                                  <span className={isNa ? "text-text-muted line-through" : "text-text"}>{s.display_name}</span>
                                  {isNa && (
                                    <span className="text-[9px] font-mono uppercase text-text-dim border border-line rounded px-1 tracking-wider2">N/A</span>
                                  )}
                                  {!isNa && s.allow_free_text && (
                                    <span className="text-[9px] font-mono uppercase text-accent tracking-wider2">free-text</span>
                                  )}
                                </div>
                                {!isNa && (() => { const ls = hopCounts(hopLocalData, "sub", s.pack_subcategory_id); return <HopWaitingChip count={ls.waiting} lost={ls.lost} />; })()}
                                {hasPhotos && !isNa && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-ok/10 border border-ok/30 text-[10px] font-mono text-ok flex-shrink-0">
                                    <CheckCircle2 size={10} />
                                    {subCount}
                                  </span>
                                )}
                              </div>
                              {s.hint && !isNa && (
                                <div className="text-[11px] text-text-muted mt-0.5 line-clamp-3">{s.hint}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {/* T-219 — Categories editor modal */}
      {showCategoriesEditor && pack && (
        <CategoriesEditorModal
          pack={pack}
          categories={categories}
          subcategories={subcats}
          onClose={() => setShowCategoriesEditor(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["hop_pack_categories_for_pack", packId] });
            qc.invalidateQueries({ queryKey: ["hop_pack_subcategories_for_pack", packId] });
          }}
        />
      )}

      {portalOpen && projectId && (
        <HopShareModal projectId={projectId} onClose={() => setPortalOpen(false)} />
      )}
    </div>
  );
}

function StatTile({
  label, value, icon: Icon, ok,
}: {
  label: string; value: string; icon: any; ok?: boolean;
}) {
  return (
    <Card>
      <div className="p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">{label}</div>
          <div className={`w-6 h-6 rounded border flex items-center justify-center ${ok ? "bg-ok/10 text-ok border-ok/30" : "bg-accent/10 text-accent border-accent/20"}`}>
            <Icon size={11} />
          </div>
        </div>
        <div className={`text-[18px] font-semibold ${ok ? "text-ok" : "text-text"}`}>{value}</div>
      </div>
    </Card>
  );
}

// =============================================================================
// CategoriesEditorModal — T-219
// Manager+ only. Add / rename / delete pack categories. Delete with photos
// requires picking a target category to move them to (uses
// app.delete_pack_category_with_move RPC). v1 scope: categories only;
// subcategory editing + reorder are follow-up polish.
// =============================================================================
function CategoriesEditorModal({
  pack, categories, subcategories, onClose, onSaved,
}: {
  pack: Pack;
  categories: PackCategory[];
  subcategories: PackSubcategory[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [moveTargetId, setMoveTargetId] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  // T-219 v2 — add + rename free-text sub-categories on an existing category
  const [expandedCatId, setExpandedCatId] = useState<string | null>(null);
  const [newSubName, setNewSubName] = useState("");
  const [newSubCode, setNewSubCode] = useState("");
  const [editingSubId, setEditingSubId] = useState<string | null>(null);
  const [editingSubName, setEditingSubName] = useState("");
  const [editingSubCode, setEditingSubCode] = useState("");

  // Photo counts per category — drives the delete-with-move warning
  const { data: photoCounts = {} } = useQuery({
    queryKey: ["hop_pack_category_photo_counts", pack.pack_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_assets")
        .select("pack_category_id")
        .eq("pack_id", pack.pack_id);
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of data ?? []) {
        const id = (row as any).pack_category_id;
        if (id) counts[id] = (counts[id] ?? 0) + 1;
      }
      return counts;
    },
  });

  const addMut = useMutation({
    mutationFn: async () => {
      if (!newName.trim()) throw new Error("Display name required");
      const code = (newCode.trim() || newName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""));
      if (!code) throw new Error("Couldn't generate a code from the name");
      const maxSort = categories.reduce((m, c) => Math.max(m, c.sort_order), 0);
      const { error } = await supabase
        .from("hop_pack_categories")
        .insert({
          pack_id:      pack.pack_id,
          code,
          display_name: newName.trim(),
          sort_order:   maxSort + 10,
          is_custom:    true,
        });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      setNewName(""); setNewCode(""); setShowAddForm(false); setErr(null);
      onSaved();
    },
    onError: (e: any) => setErr(e.message ?? "Add failed"),
  });

  const renameMut = useMutation({
    mutationFn: async (args: { id: string; name: string }) => {
      const { error } = await supabase
        .from("hop_pack_categories")
        .update({ display_name: args.name.trim() })
        .eq("pack_category_id", args.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { setEditingId(null); setEditingName(""); setErr(null); onSaved(); },
    onError: (e: any) => setErr(e.message ?? "Rename failed"),
  });

  const deleteMut = useMutation({
    mutationFn: async (args: { id: string; moveTo: string | null }) => {
      const { error } = await supabase.schema("app").rpc("delete_pack_category_with_move", {
        p_pack_category_id:         args.id,
        // T-280 boy-scout: generated RPC type wants string | undefined for
        // optional params; coerce null → undefined so typecheck stops
        // complaining without changing runtime behaviour (RPC + Postgres
        // function both accept null/undefined identically as "no move").
        p_move_to_pack_category_id: args.moveTo ?? undefined,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { setDeletingId(null); setMoveTargetId(""); setErr(null); onSaved(); },
    onError: (e: any) => setErr(e.message ?? "Delete failed"),
  });

  // T-219 v2 — add a free-text sub-category to an existing category (like General).
  const addSubMut = useMutation({
    mutationFn: async (args: { categoryId: string; name: string; code?: string }) => {
      const name = args.name.trim();
      if (!name) throw new Error("Sub-category name required");
      const catSubs = subcategories.filter((s: any) => s.pack_category_id === args.categoryId);
      // T-219 v3 — the explicit "file ID" box (first box) drives the export
      // filename. Use it verbatim when set; otherwise slug the display name.
      const typed = (args.code ?? "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
      const base = typed || name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "sub";
      const code = catSubs.some((s: any) => s.code === base) ? `${base}_${catSubs.length + 1}` : base;
      const maxSort = catSubs.reduce((m: number, s: any) => Math.max(m, s.sort_order ?? 0), 0);
      const { error } = await supabase
        .from("hop_pack_subcategories")
        .insert({
          pack_category_id: args.categoryId,
          code,
          display_name: name,
          allow_free_text: true,
          sort_order: maxSort + 10,
          is_custom: true,
        });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { setNewSubName(""); setNewSubCode(""); setErr(null); onSaved(); },
    onError: (e: any) => setErr(e.message ?? "Add sub-category failed"),
  });

  // T-219 v2 — rename a sub-category (fix a mistake). For CUSTOM subs we also
  // regenerate the code (drives the export filename); template subs keep their
  // seeded code so we never break the carrier naming convention.
  const renameSubMut = useMutation({
    mutationFn: async (args: { sub: any; name: string; code?: string }) => {
      const name = args.name.trim();
      if (!name) throw new Error("Sub-category name required");
      const patch: any = { display_name: name };
      // T-219 v3 — for CUSTOM subs the "file ID" box (first box) drives the
      // export filename. Use it verbatim when set; otherwise fall back to a
      // slug of the display name. Template subs keep their seeded code so we
      // never break the carrier naming convention.
      if (args.sub.is_custom) {
        const typed = (args.code ?? "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
        const siblings = subcategories.filter((s: any) =>
          s.pack_category_id === args.sub.pack_category_id && s.pack_subcategory_id !== args.sub.pack_subcategory_id);
        const base = typed || name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "sub";
        patch.code = siblings.some((s: any) => s.code === base) ? `${base}_${siblings.length + 1}` : base;
      }
      const { error } = await supabase
        .from("hop_pack_subcategories")
        .update(patch)
        .eq("pack_subcategory_id", args.sub.pack_subcategory_id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { setEditingSubId(null); setEditingSubName(""); setEditingSubCode(""); setErr(null); onSaved(); },
    onError: (e: any) => setErr(e.message ?? "Rename failed"),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-md w-full max-w-[640px] my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <div className="font-semibold">Edit categories</div>
            <div className="text-[11px] text-text-muted mt-0.5">{pack.label_prefix ?? pack.pack_number}</div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-2.5 -m-2.5 rounded-md" type="button"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-3">
          {err && (
            <div className="text-[12px] text-lost border border-lost/30 bg-lost/10 rounded px-3 py-2">{err}</div>
          )}

          {categories.map(c => {
            const photoCount = photoCounts[c.pack_category_id] ?? 0;
            const isDeleting = deletingId === c.pack_category_id;
            const isEditing  = editingId  === c.pack_category_id;
            const subCount   = subcategories.filter(s => s.pack_category_id === c.pack_category_id).length;
            return (
              <div key={c.pack_category_id} className="border border-line rounded p-3 bg-ink-raised/40">
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <input
                        autoFocus
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") renameMut.mutate({ id: c.pack_category_id, name: editingName });
                          if (e.key === "Escape") { setEditingId(null); setEditingName(""); }
                        }}
                        className="flex-1 bg-ink border border-line rounded px-2 py-1 text-[13px]"
                      />
                      <button
                        onClick={() => renameMut.mutate({ id: c.pack_category_id, name: editingName })}
                        className="text-ok hover:opacity-80"
                        title="Save"
                      ><CheckCircle2 size={14}/></button>
                      <button
                        onClick={() => { setEditingId(null); setEditingName(""); }}
                        className="text-text-muted hover:text-text"
                        title="Cancel"
                      ><X size={14}/></button>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-[10px] uppercase text-text-dim w-20 truncate">{c.code}</span>
                      <span className="flex-1 text-[13px] text-text">{c.display_name}</span>
                      <span className="text-[10px] text-text-muted">
                        {photoCount} photo{photoCount === 1 ? "" : "s"}{subCount > 0 ? ` · ${subCount} sub` : ""}
                      </span>
                      <button
                        onClick={() => { setEditingId(c.pack_category_id); setEditingName(c.display_name); }}
                        className="text-text-muted hover:text-text"
                        title="Rename"
                      ><Edit3 size={12}/></button>
                      <button
                        onClick={() => { setDeletingId(c.pack_category_id); setMoveTargetId(""); }}
                        className="text-text-muted hover:text-lost"
                        title="Delete"
                      ><Trash2 size={12}/></button>
                    </>
                  )}
                </div>

                {/* Delete confirmation row */}
                {isDeleting && (
                  <div className="mt-3 pt-3 border-t border-line space-y-2">
                    {photoCount > 0 ? (
                      <>
                        <div className="text-[12px] text-warn">
                          <AlertTriangle size={11} className="inline mr-1"/>
                          {photoCount} photo{photoCount === 1 ? "" : "s"} in this category. Pick a target to move them to:
                        </div>
                        <select
                          value={moveTargetId}
                          onChange={e => setMoveTargetId(e.target.value)}
                          className="w-full bg-ink border border-line rounded px-2 py-1 text-[13px]"
                        >
                          <option value="">— pick target —</option>
                          {categories
                            .filter(t => t.pack_category_id !== c.pack_category_id)
                            .map(t => <option key={t.pack_category_id} value={t.pack_category_id}>{t.display_name}</option>)}
                        </select>
                      </>
                    ) : (
                      <div className="text-[12px] text-text-muted">
                        Empty category — safe to delete.
                      </div>
                    )}
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="secondary" onClick={() => { setDeletingId(null); setMoveTargetId(""); }} disabled={deleteMut.isPending}>
                        Cancel
                      </Button>
                      <Button
                        icon={Trash2}
                        variant="danger"
                        onClick={() => deleteMut.mutate({
                          id: c.pack_category_id,
                          moveTo: photoCount > 0 ? (moveTargetId || null) : null,
                        })}
                        disabled={deleteMut.isPending || (photoCount > 0 && !moveTargetId)}
                      >
                        {deleteMut.isPending ? "Deleting…" : photoCount > 0 ? `Move ${photoCount} & delete` : "Delete"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* T-219 v2 — sub-categories: add free-text lines (like General) */}
                {!isDeleting && !isEditing && (
                  <div className="mt-2 pt-2 border-t border-line/50">
                    <button type="button"
                      onClick={() => { setExpandedCatId(expandedCatId === c.pack_category_id ? null : c.pack_category_id); setNewSubName(""); }}
                      className="text-[11px] text-text-muted hover:text-accent inline-flex items-center gap-1">
                      <ChevronDown size={11} className={`transition-transform ${expandedCatId === c.pack_category_id ? "" : "-rotate-90"}`}/>
                      {subCount} sub-categor{subCount === 1 ? "y" : "ies"} — add one
                    </button>
                    {expandedCatId === c.pack_category_id && (
                      <div className="mt-2 space-y-1 pl-4">
                        {subcategories
                          .filter((s: any) => s.pack_category_id === c.pack_category_id)
                          .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                          .map((s: any) => (
                            <div key={s.pack_subcategory_id} className="flex items-center gap-2 text-[12px]">
                              {editingSubId === s.pack_subcategory_id ? (
                                <>
                                  <input autoFocus value={editingSubName}
                                    onChange={e => setEditingSubName(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === "Enter" && editingSubName.trim()) renameSubMut.mutate({ sub: s, name: editingSubName, code: editingSubCode });
                                      if (e.key === "Escape") { setEditingSubId(null); setEditingSubName(""); setEditingSubCode(""); }
                                    }}
                                    placeholder="Display name (e.g. Member 258)"
                                    className="flex-1 bg-ink border border-line rounded px-2 py-1 text-[12px]" />
                                  {s.is_custom && (
                                    <input value={editingSubCode}
                                      onChange={e => setEditingSubCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                                      onKeyDown={e => {
                                        if (e.key === "Enter" && editingSubName.trim()) renameSubMut.mutate({ sub: s, name: editingSubName, code: editingSubCode });
                                        if (e.key === "Escape") { setEditingSubId(null); setEditingSubName(""); setEditingSubCode(""); }
                                      }}
                                      placeholder="file ID — 258"
                                      title="Appears in the client export filename. Leave blank to auto-fill from the name."
                                      className="w-24 bg-ink border border-line rounded px-2 py-1 text-[12px] font-mono" />
                                  )}
                                  <button onClick={() => renameSubMut.mutate({ sub: s, name: editingSubName, code: editingSubCode })} className="text-ok hover:opacity-80" title="Save"><CheckCircle2 size={13}/></button>
                                  <button onClick={() => { setEditingSubId(null); setEditingSubName(""); setEditingSubCode(""); }} className="text-text-muted hover:text-text" title="Cancel"><X size={13}/></button>
                                </>
                              ) : (
                                <>
                                  <span className="text-text flex-1">{s.display_name}</span>
                                  {s.is_custom && <span className="font-mono text-[10px] uppercase text-text-dim" title="File ID — appears in the client export filename">{s.code}</span>}
                                  {s.allow_free_text && <span className="text-[9px] uppercase tracking-wider text-text-dim">free text</span>}
                                  <button onClick={() => { setEditingSubId(s.pack_subcategory_id); setEditingSubName(s.display_name); setEditingSubCode(s.is_custom ? s.code : ""); }} className="text-text-muted hover:text-text" title="Rename"><Edit3 size={11}/></button>
                                </>
                              )}
                            </div>
                          ))}
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            value={newSubName}
                            onChange={e => setNewSubName(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter" && newSubName.trim()) addSubMut.mutate({ categoryId: c.pack_category_id, name: newSubName, code: newSubCode }); }}
                            placeholder="New sub-category — e.g. Member 258"
                            className="flex-1 bg-ink border border-line rounded px-2 py-1 text-[12px]"
                          />
                          <input
                            value={newSubCode}
                            onChange={e => setNewSubCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                            onKeyDown={e => { if (e.key === "Enter" && newSubName.trim()) addSubMut.mutate({ categoryId: c.pack_category_id, name: newSubName, code: newSubCode }); }}
                            placeholder="file ID — 258"
                            title="Appears in the client export filename. Leave blank to auto-fill from the name."
                            className="w-24 bg-ink border border-line rounded px-2 py-1 text-[12px] font-mono"
                          />
                          <Button size="sm" icon={Plus}
                            disabled={addSubMut.isPending || !newSubName.trim()}
                            onClick={() => addSubMut.mutate({ categoryId: c.pack_category_id, name: newSubName, code: newSubCode })}>
                            {addSubMut.isPending ? "Adding…" : "Add"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add category form */}
          {showAddForm ? (
            <div className="border border-accent/30 rounded p-3 bg-accent/5 space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">New category</div>
              <input
                autoFocus
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Display name (e.g. Earthing photos)"
                className="w-full bg-ink border border-line rounded px-2 py-1 text-[13px]"
              />
              <input
                value={newCode}
                onChange={e => setNewCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                placeholder="Code (auto if blank — e.g. earthing_photos)"
                className="w-full bg-ink border border-line rounded px-2 py-1 text-[13px] font-mono"
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="secondary" onClick={() => { setShowAddForm(false); setNewName(""); setNewCode(""); }} disabled={addMut.isPending}>
                  Cancel
                </Button>
                <Button icon={Plus} onClick={() => addMut.mutate()} disabled={addMut.isPending || !newName.trim()}>
                  {addMut.isPending ? "Adding…" : "Add category"}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="w-full border border-dashed border-line rounded p-3 text-[12px] text-text-muted hover:border-accent hover:text-accent inline-flex items-center justify-center gap-2"
            >
              <Plus size={12}/> Add category
            </button>
          )}
        </div>

        <div className="px-5 py-3 border-t border-line bg-ink-raised flex items-center justify-between">
          <div className="text-[10px] text-text-dim italic">
            Expand a category to add or rename its free-text sub-categories (T-219 v2). Reorder comes later.
          </div>
          <Button variant="secondary" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}
